import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import analytics from './analytics';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const zodiacSigns = [
  { value: 'aries', label: 'Koç', icon: '♈', element: 'fire', color: '#ef4444' },
  { value: 'taurus', label: 'Boğa', icon: '♉', element: 'earth', color: '#22c55e' },
  { value: 'gemini', label: 'İkizler', icon: '♊', element: 'air', color: '#eab308' },
  { value: 'cancer', label: 'Yengeç', icon: '♋', element: 'water', color: '#3b82f6' },
  { value: 'leo', label: 'Aslan', icon: '♌', element: 'fire', color: '#f97316' },
  { value: 'virgo', label: 'Başak', icon: '♍', element: 'earth', color: '#84cc16' },
  { value: 'libra', label: 'Terazi', icon: '♎', element: 'air', color: '#ec4899' },
  { value: 'scorpio', label: 'Akrep', icon: '♏', element: 'water', color: '#dc2626' },
  { value: 'sagittarius', label: 'Yay', icon: '♐', element: 'fire', color: '#8b5cf6' },
  { value: 'capricorn', label: 'Oğlak', icon: '♑', element: 'earth', color: '#6b7280' },
  { value: 'aquarius', label: 'Kova', icon: '♒', element: 'air', color: '#06b6d4' },
  { value: 'pisces', label: 'Balık', icon: '♓', element: 'water', color: '#6366f1' },
];

// Score display component with animation
const ScoreCard = ({ label, value, icon, delay = 0 }) => {
  const getColor = (val) => {
    if (val >= 80) return '#4ade80';
    if (val >= 60) return '#fbbf24';
    if (val >= 40) return '#fb923c';
    return '#f87171';
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.8 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ delay }}
      style={{
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.1)',
        borderRadius: '12px',
        padding: '12px',
        textAlign: 'center',
        position: 'relative',
        overflow: 'hidden'
      }}
    >
      {/* Progress bar background */}
      <div style={{
        position: 'absolute',
        bottom: 0,
        left: 0,
        width: `${value}%`,
        height: '3px',
        background: `linear-gradient(90deg, ${getColor(value)}40, ${getColor(value)})`,
        transition: 'width 1s ease-out'
      }} />
      
      <div style={{ fontSize: '20px', marginBottom: '4px' }}>{icon}</div>
      <div style={{ 
        fontSize: '24px', 
        fontWeight: 'bold', 
        color: getColor(value),
        textShadow: `0 0 20px ${getColor(value)}40`
      }}>
        %{value}
      </div>
      <div style={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', marginTop: '2px' }}>
        {label}
      </div>
    </motion.div>
  );
};

// Compatibility heart animation
const CompatibilityHeart = ({ personA, personB, overallScore }) => {
  const signA = zodiacSigns.find(z => z.value === personA.sign);
  const signB = zodiacSigns.find(z => z.value === personB.sign);
  
  return (
    <motion.div
      initial={{ scale: 0 }}
      animate={{ scale: 1 }}
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '16px',
        marginBottom: '24px',
        padding: '24px',
        background: 'linear-gradient(135deg, rgba(236,72,153,0.1), rgba(139,92,246,0.1))',
        borderRadius: '20px',
        border: '1px solid rgba(236,72,153,0.2)'
      }}
    >
      {/* Person A */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ 
          fontSize: '48px', 
          filter: `drop-shadow(0 0 10px ${signA?.color || '#8b5cf6'})` 
        }}>
          {signA?.icon || '♈'}
        </div>
        <div style={{ color: 'white', fontWeight: '600', marginTop: '4px' }}>
          {personA.name || signA?.label}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
          {signA?.label}
        </div>
      </div>

      {/* Heart with score */}
      <div style={{ position: 'relative' }}>
        <motion.div
          animate={{ scale: [1, 1.1, 1] }}
          transition={{ repeat: Infinity, duration: 1.5 }}
          style={{ fontSize: '64px', color: '#ec4899' }}
        >
          💕
        </motion.div>
        <div style={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          background: 'rgba(0,0,0,0.7)',
          borderRadius: '50%',
          width: '40px',
          height: '40px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 'bold',
          fontSize: '12px',
          color: '#ec4899'
        }}>
          {overallScore}%
        </div>
      </div>

      {/* Person B */}
      <div style={{ textAlign: 'center' }}>
        <div style={{ 
          fontSize: '48px', 
          filter: `drop-shadow(0 0 10px ${signB?.color || '#8b5cf6'})` 
        }}>
          {signB?.icon || '♈'}
        </div>
        <div style={{ color: 'white', fontWeight: '600', marginTop: '4px' }}>
          {personB.name || signB?.label}
        </div>
        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '12px' }}>
          {signB?.label}
        </div>
      </div>
    </motion.div>
  );
};

const CompatibilityTab = ({ user, onCreditUsed }) => {
  const [personA, setPersonA] = useState({ name: '', sign: '', birthdate: '' });
  const [personB, setPersonB] = useState({ name: '', sign: '', birthdate: '' });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  const creditCost = 6;
  // Elite/Pro users have unlimited credits
  const isUnlimited = user.subscription_type === 'elite' || user.subscription_type === 'pro' || user.credits === -1;
  const hasEnoughCredits = isUnlimited || (user.credits || 0) >= creditCost;

  const handleAnalyze = async () => {
    if (!personA.sign || !personB.sign) return;
    analytics.compatibilityStarted();
    setLoading(true);
    setResult(null);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(`${API_URL}/api/compatibility/analysis`, {
        person_a: personA,
        person_b: personB,
        language: 'tr'
      }, { headers: { Authorization: `Bearer ${token}` } });
      setResult(data);
      analytics.compatibilityCompleted();
      if (onCreditUsed) onCreditUsed();
    } catch (err) {
      setResult({ error: err.response?.data?.detail || 'Bir hata oluştu' });
    } finally {
      setLoading(false);
    }
  };

  // Sign picker component - extracted outside to prevent re-renders
  const renderSignPicker = (person, setPerson, label, testIdSuffix) => (
    <div style={{ flex: 1, minWidth: '280px' }}>
      <h3 style={{ color: '#ec4899', fontSize: '16px', marginBottom: '12px', textAlign: 'center' }}>
        {label}
      </h3>
      <input
        type="text"
        placeholder="İsim (opsiyonel)"
        value={person.name}
        onChange={(e) => setPerson({ ...person, name: e.target.value })}
        data-testid={`compatibility-name-${testIdSuffix}`}
        style={{
          width: '100%', padding: '12px', marginBottom: '10px', boxSizing: 'border-box',
          background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.12)',
          borderRadius: '10px', color: 'white', fontSize: '14px'
        }}
      />
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '6px' }}>
        {zodiacSigns.map((z) => (
          <button
            key={z.value}
            onClick={() => setPerson({ ...person, sign: z.value })}
            data-testid={`compatibility-sign-${testIdSuffix}-${z.value}`}
            style={{
              padding: '10px 4px',
              background: person.sign === z.value 
                ? `linear-gradient(135deg, ${z.color}40, ${z.color}20)` 
                : 'rgba(255,255,255,0.03)',
              border: person.sign === z.value 
                ? `2px solid ${z.color}` 
                : '1px solid rgba(255,255,255,0.1)',
              borderRadius: '10px',
              cursor: 'pointer',
              textAlign: 'center',
              transition: 'all 0.2s'
            }}
          >
            <div style={{ 
              fontSize: '24px',
              filter: person.sign === z.value ? `drop-shadow(0 0 8px ${z.color})` : 'none'
            }}>
              {z.icon}
            </div>
            <div style={{ 
              fontSize: '10px', 
              color: person.sign === z.value ? 'white' : 'rgba(255,255,255,0.5)', 
              marginTop: '2px' 
            }}>
              {z.label}
            </div>
          </button>
        ))}
      </div>
    </div>
  );

  // Form view
  if (!result || result.error) {
    return (
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} className="tab-content">
        <div style={{ textAlign: 'center', marginBottom: '24px' }}>
          <h2 style={{ color: 'white', marginBottom: '8px', fontSize: '24px' }}>
            💕 Premium Aşk Uyumu Analizi
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '14px' }}>
            Duygusal uyum • İletişim • Tutku • Evlilik potansiyeli • 5 özel tavsiye
          </p>
        </div>

        {/* Premium features */}
        <div style={{
          background: 'linear-gradient(90deg, rgba(236,72,153,0.1), rgba(139,92,246,0.1))',
          border: '1px solid rgba(236,72,153,0.2)',
          borderRadius: '12px',
          padding: '12px',
          marginBottom: '20px'
        }}>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
            {['6 Uyum Skoru', 'Detaylı Analiz', 'Zorluk Alanları', '5 Tavsiye'].map((f, i) => (
              <span key={i} style={{
                background: 'rgba(236,72,153,0.2)',
                padding: '4px 10px',
                borderRadius: '12px',
                fontSize: '11px',
                color: '#f472b6'
              }}>
                ✓ {f}
              </span>
            ))}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginBottom: '20px' }}>
          {renderSignPicker(personA, setPersonA, '👤 Kişi A', 'a')}
          <div style={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'center', 
            fontSize: '40px', 
            padding: '0 8px',
            minWidth: '60px'
          }}>
            <motion.span 
              animate={{ scale: [1, 1.2, 1] }}
              transition={{ repeat: Infinity, duration: 1.5 }}
              style={{ color: '#ec4899' }}
            >
              💕
            </motion.span>
          </div>
          {renderSignPicker(personB, setPersonB, '👤 Kişi B', 'b')}
        </div>

        <button
          className="btn-primary btn-large"
          onClick={handleAnalyze}
          disabled={!personA.sign || !personB.sign || loading || !hasEnoughCredits}
          data-testid="compatibility-analyze-button"
          style={{ 
            width: '100%',
            background: 'linear-gradient(135deg, #ec4899 0%, #8b5cf6 100%)',
            padding: '16px',
            fontSize: '16px'
          }}
        >
          {loading ? (
            <span>💫 Uyum analiz ediliyor...</span>
          ) : (
            `Aşk Uyumunu Analiz Et (${creditCost} 💎)`
          )}
        </button>

        {!hasEnoughCredits && (
          <div style={{ marginTop: '12px', padding: '12px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', textAlign: 'center', color: 'rgba(255,255,255,0.6)', fontSize: '14px' }}>
            Yetersiz kredi. Bu analiz {creditCost} kredi gerektirir.
          </div>
        )}

        {result?.error && (
          <div style={{ marginTop: '16px', padding: '14px', background: 'rgba(239,68,68,0.1)', borderRadius: '10px', color: '#f87171', textAlign: 'center' }}>
            {result.error}
          </div>
        )}
      </motion.div>
    );
  }

  // Result view - Premium
  const scoreLabels = {
    overall: { label: 'Genel Uyum', icon: '💫' },
    emotional: { label: 'Duygusal', icon: '🌊' },
    communication: { label: 'İletişim', icon: '💬' },
    passion: { label: 'Tutku', icon: '🔥' },
    long_term: { label: 'Uzun Vade', icon: '🌟' },
    marriage: { label: 'Evlilik', icon: '💍' }
  };

  return (
    <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="tab-content">
      {/* Header */}
      <div style={{ textAlign: 'center', marginBottom: '16px' }}>
        <h2 style={{ color: 'white', marginBottom: '4px' }}>💕 Aşk Uyumu Sonuçları</h2>
      </div>

      {/* Compatibility Heart */}
      <CompatibilityHeart 
        personA={personA} 
        personB={personB} 
        overallScore={result.scores?.overall || result.scores?.love || 75}
      />

      {/* Score Cards */}
      {result.scores && (
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(3, 1fr)', 
          gap: '10px', 
          marginBottom: '20px' 
        }}>
          {Object.entries(result.scores)
            .filter(([key]) => scoreLabels[key])
            .map(([key, val], i) => (
              <ScoreCard
                key={key}
                label={scoreLabels[key].label}
                value={val}
                icon={scoreLabels[key].icon}
                delay={i * 0.1}
              />
            ))}
        </div>
      )}

      {/* Analysis Content */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.5 }}
        style={{
          background: 'linear-gradient(135deg, rgba(236,72,153,0.08), rgba(139,92,246,0.05))',
          border: '1px solid rgba(236,72,153,0.2)',
          borderRadius: '16px',
          padding: '24px'
        }}
      >
        <div 
          data-testid="compatibility-result" 
          style={{ 
            whiteSpace: 'pre-wrap', 
            lineHeight: 1.9, 
            color: 'rgba(255,255,255,0.85)', 
            fontSize: '15px' 
          }}
        >
          {result.analysis}
        </div>
      </motion.div>

      {/* Back button */}
      <button
        onClick={() => setResult(null)}
        style={{
          marginTop: '20px',
          padding: '12px 24px',
          background: 'rgba(255,255,255,0.1)',
          border: '1px solid rgba(255,255,255,0.2)',
          borderRadius: '10px',
          color: 'white',
          cursor: 'pointer',
          width: '100%'
        }}
      >
        ← Yeni Analiz Yap
      </button>
    </motion.div>
  );
};

export default CompatibilityTab;
